import { FC, useContext, useEffect, useState } from "react";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import { useI18n } from "../../hooks/useTranslations";
import { useClientsApi } from "../../http/clients/api";
import { FullClient, UpsertClientPermissionsBody } from "../../http/clients/types";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import FormFieldsContainer from "../ui/FormFieldsContainer";
import MultiCheckboxPermission from "../ui/MultiCheckboxPermission";

export interface ClientPermissionsFormBody {
  permissions: string[];
}

type ClientPermissionsFormProps = {
  isDisabled?: boolean;
  isLoading?: boolean;
  client: FullClient;
};

const ClientPermissionsForm: FC<ClientPermissionsFormProps> = ({
  isDisabled,
  isLoading,
  client,
}: ClientPermissionsFormProps) => {
  const i18n = useI18n();
  const clientsApi = useClientsApi();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const defaultValues = {
    permissions: client?.permissions ?? [],
  };

  const onSubmit = async (formData: ClientPermissionsFormBody) => {
    const body: UpsertClientPermissionsBody = {
      permissions: formData.permissions,
    };
    setIsSubmitting(true);

    try {
      await clientsApi.upsertAdminClientPermissions(client.id, body);
      dispatch(showSuccessNotification(i18n.t));
    } catch (error) {
      dispatch(showErrorNotification(i18n, error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Form<ClientPermissionsFormBody>
        submitText={i18n.t.common.save}
        disabled={isDisabled || isSubmitting}
        onSubmit={() => onSubmit}
        defaultValues={defaultValues}
      >
        {({ setValue, watch, reset }) => {
          const permissionsSelected = watch("permissions");

          useEffect(() => {
            reset(defaultValues);
          }, [client]);

          const isCheckBoxDisabled = (permissions: string[], permissionPrefix: string, permissionAction: string) => {
            return (
              !permissions.some((p) => p === permissionAction) ||
              (permissionAction === "read" &&
                permissionsSelected.some(
                  (p) => p.split(":")[0] === permissionPrefix && p !== permissionPrefix + ":read",
                ))
            );
          };

          const onPermissionRowClick = (permissions: string[], resource: string) => {
            const rowPermissions = ["read", "insert", "update", "delete"].map((op) => `${resource}:${op}`);

            const hasAny = rowPermissions.some((perm) => permissionsSelected.includes(perm));

            if (hasAny) {
              setValue(
                "permissions",
                permissionsSelected.filter((p) => p.split(":")[0] !== resource),
              );
            } else {
              let rolesToAdd: string[] = [];
              rowPermissions.forEach((permission) => {
                !isCheckBoxDisabled(permissions, permission.split(":")[0] ?? "", permission.split(":")[1] ?? "") &&
                  rolesToAdd.push(permission);
              });
              setValue("permissions", [...permissionsSelected, ...rolesToAdd]);
            }
          };

          const isCheckBoxChecked = (permission: string) => {
            return permissionsSelected.includes(permission);
          };

          const onCheckBoxChange = (e: React.ChangeEvent<HTMLInputElement>, permission: string) => {
            const newPermissions = e.target.checked
              ? [...permissionsSelected, permission]
              : permissionsSelected.filter((p) => p !== permission);

            if (permission.split(":")[1] !== "read") {
              !newPermissions.find((p) => permission.split(":")[0] + ":read" === p) &&
                newPermissions.push(permission.split(":")[0] + ":read");
            }

            setValue("permissions", newPermissions);
          };

          return (
            <FormFieldsContainer>
              <FormField>
                <MultiCheckboxPermission
                  isDisabled={isDisabled || isLoading}
                  onPermissionRowClick={onPermissionRowClick}
                  isCheckBoxDisabled={isCheckBoxDisabled}
                  isCheckBoxChecked={isCheckBoxChecked}
                  onCheckBoxChange={onCheckBoxChange}
                  showClients={false}
                />
              </FormField>
            </FormFieldsContainer>
          );
        }}
      </Form>
    </>
  );
};

export default ClientPermissionsForm;
